import { Box, Flex, HStack, Icon, Text, useToast } from '@chakra-ui/react'
import { t } from '@lingui/core/macro'
import { Trans } from '@lingui/react/macro'
import { useEffect, useRef, useState, type FC } from 'react'
import { MdKeyboardArrowUp } from 'react-icons/md'

import { useCaptureEvent } from '../utils/analytics'
import { ThumbsButtons } from './ThumbsButtons'

type Props = {
  customSummary: string
  rootCause?: string
  conversationUid: string
}
const MAX_SUMMARY_HEIGHT = 190

const SummaryBox: FC<Props> = ({
  customSummary,
  rootCause,
  conversationUid,
}) => {
  const [show, setShow] = useState(false)
  const ref = useRef<HTMLParagraphElement>(null)
  const [height, setHeight] = useState(0)
  const forceShow = MAX_SUMMARY_HEIGHT > (ref.current?.offsetHeight || 0)

  const toast = useToast()
  const [isReviewed, setIsReviewed] = useState(false)
  const captureEvent = useCaptureEvent()

  const handleToggle = (): void => {
    const newShowState = !show
    setHeight(ref.current?.offsetHeight || MAX_SUMMARY_HEIGHT)
    setShow(newShowState)

    // Capture PostHog event
    captureEvent('previous_call_summary_toggle', {
      action: newShowState ? 'expand' : 'collapse',
    })
  }
  useEffect(() => {
    setHeight(ref.current?.offsetHeight || MAX_SUMMARY_HEIGHT)
  }, [])

  return (
    <Box
      onClick={handleToggle}
      background="white"
      borderColor="greys.100"
      position="relative"
      overflow="hidden"
      borderRadius="0 0 8px 8px"
      w="100%"
      cursor={forceShow ? undefined : 'pointer'}
      _hover={{
        background: forceShow ? undefined : 'greys.100',
      }}
    >
      <Flex
        alignItems="start"
        gap={1}
        px={4}
        py={2}
        pb={forceShow || show ? 2 : 0}
        w="100%"
      >
        <Box w="100%">
          <Box
            transition="height 200ms"
            height={forceShow || show ? height : MAX_SUMMARY_HEIGHT}
            overflow="hidden"
          >
            <HStack align="flex-start">
              <Flex gap="3" flexDir="column" ref={ref}>
                <Box>
                  <Text
                    color="greys.400"
                    fontSize="sm"
                    fontWeight="semibold"
                    textTransform="uppercase"
                  >
                    <Trans>Call reason description</Trans>
                  </Text>
                  <Text
                    color="greys.600"
                    fontWeight="300"
                    whiteSpace="break-spaces"
                  >
                    {rootCause}
                  </Text>
                </Box>
                <Box>
                  <Text
                    color="greys.400"
                    fontSize="sm"
                    fontWeight="semibold"
                    textTransform="uppercase"
                  >
                    <Trans>Additional details</Trans>
                  </Text>
                  <Text
                    color="greys.600"
                    fontWeight="300"
                    whiteSpace="break-spaces"
                  >
                    {customSummary}
                  </Text>
                </Box>
              </Flex>
              {!isReviewed && (
                <HStack gap="1">
                  <ThumbsButtons
                    onUp={() => {
                      captureEvent('widgetsummary_ThumbsUp', {
                        summary: customSummary,
                        rootCause: rootCause || '',
                        conversationUid,
                      })
                      setIsReviewed(true)
                      toast({
                        title: t`Thank you for the feedback`,
                        description: t`You are helping us to improve future summaries`,
                        status: 'success',
                        duration: 4000,
                        isClosable: true,
                      })
                    }}
                    onDown={() => {
                      captureEvent('widgetsummary_ThumbsDown', {
                        summary: customSummary,
                        rootCause: rootCause || '',
                        conversationUid,
                      })
                      setIsReviewed(true)
                      toast({
                        title: t`Thank you for the feedback`,
                        description: t`You are helping us to improve future summaries`,
                        status: 'success',
                        duration: 4000,
                        isClosable: true,
                      })
                    }}
                  />
                </HStack>
              )}
            </HStack>
          </Box>
        </Box>
      </Flex>
      {!forceShow && (
        <Box
          transition="height 300ms"
          id="gradientBox"
          pos={show ? 'static' : 'absolute'}
          bottom="0px"
          w="100%"
          h={show ? '12px' : '50px'}
          background={
            show
              ? 'none'
              : 'linear-gradient(180deg, rgba(255, 255, 255, 0) 0%, white 100%)'
          }
        >
          <Icon
            transition="transform 500ms"
            transform={show ? 'scale(1, 1)' : 'scale(1, -1)'}
            pos="absolute"
            left="50%"
            bottom="4px"
            as={MdKeyboardArrowUp}
            fontSize="20px"
          />
        </Box>
      )}
    </Box>
  )
}

export default SummaryBox
